import { useMemo, useState, type ReactNode } from 'react';
import { AppDataGrid, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar } from '../../components/AppToolbar';
import { ExportMenu } from './ExportMenu';

export interface DTColumn<T> {
  key: string;
  label: string;
  value: (row: T) => string;            // used for sort / filter / search / export
  render?: (row: T) => ReactNode;       // optional custom cell (defaults to value())
}

interface Props<T> {
  exportName: string;
  columns: DTColumn<T>[];
  rows: T[];
  getId: (row: T) => string;
  loading?: boolean;
  emptyText?: string;
  onNew: () => void;
  onEdit: (row: T) => void;
  onDelete: (row: T) => void;
  /** Reload the list — the canonical toolbar Refresh (BO UI Standard §6). */
  onRefresh?: () => void;
}

/**
 * List body for the accounting master pages, laid out to the BO UI Standard v2
 * vertical order (the page's FormTitle + "N items" subtitle live ABOVE, on the
 * grey page, owned by the page itself):
 *   1. the shared <AppToolbar> on its OWN full-width row — ＋ New · 🔄 Refresh
 *      left-aligned, 📤 Export to pushed hard right (.app-toolbar__spacer);
 *   2. the shared .list-search-row global search;
 *   3. ONE white grid card (.acc-card) containing ONLY the shared <AppDataGrid>
 *      (per-column header filters, click-to-sort, pagination, single footer).
 * Search filters the in-memory rows; the filtered set feeds both the grid and
 * the export. Per-row Edit/Delete stay in a non-sortable Actions render column.
 */
export function DataTable<T>({
  exportName, columns, rows, getId, loading, emptyText, onNew, onEdit, onDelete, onRefresh,
}: Props<T>) {
  const [search, setSearch] = useState('');

  // Global search across every column's display value (sort / per-column filter
  // / pagination are handled by AppDataGrid on the rows we hand it).
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => columns.some((c) => c.value(r).toLowerCase().includes(q)));
  }, [rows, columns, search]);

  // Map the accounting column config → shared-grid columns, then append the
  // Actions column (custom render, never sortable/filterable).
  const gridCols: AppColumn<T>[] = useMemo(() => {
    const mapped: AppColumn<T>[] = columns.map((c) => ({
      field: c.key,
      header: c.label,
      text: c.value,                                   // sort / filter / default cell text
      ...(c.render ? { render: c.render } : {}),
    }));
    mapped.push({
      field: '__actions',
      header: 'Actions',
      align: 'right',
      sortable: false,
      filterable: false,
      render: (r) => (
        <span onClick={(e) => e.stopPropagation()}>
          <button className="acc-row-btn" title="Edit" onClick={() => onEdit(r)}><i className="ti ti-pencil" /></button>
          <button className="acc-row-btn del" title="Delete" onClick={() => onDelete(r)}><i className="ti ti-trash" /></button>
        </span>
      ),
    });
    return mapped;
  }, [columns, onEdit, onDelete]);

  return (
    <div className="acc-body">
      {/* Action toolbar — its OWN full-width row on the grey page (never on the
          title row). Canonical order via the shared <AppToolbar>; the Export-to
          dropdown rides the shared spacer so it sits at the far right. */}
      <AppToolbar
        onNew={onNew}
        onRefresh={onRefresh}
        refreshing={loading}
        right={
          <span className="app-toolbar__spacer">
            <ExportMenu triggerClassName="ca-btn" filename={exportName}
              headers={columns.map((c) => c.label)}
              rows={visible.map((r) => columns.map((c) => c.value(r)))} />
          </span>
        }
      />
      {/* Search lives in its OWN row BELOW the toolbar (standard) — never inside the card. */}
      <div className="list-search-row">
        <div className="list-search">
          <span className="tb-i">🔍</span>
          <input placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} />
          {search && <span className="list-search-clear" onClick={() => setSearch('')}>✕</span>}
        </div>
      </div>
      {/* ONE white grid card — grid + its single footer only. */}
      <div className="acc-card">
        <AppDataGrid<T>
          rows={visible}
          columns={gridCols}
          getRowId={(r) => getId(r)}
          loading={loading}
          emptyText={emptyText}
        />
      </div>
    </div>
  );
}
